import React from 'react';
import { TasksGroup, useTasksGroupsAdminService, useTasksGroupsService } from 'nekst-api';
import {
  BaseInputProps,
  Dropdown,
  LoadingAnimation,
  useFormContextRequired
} from 'ui-builder';
import { useDataLoaderNew } from 'data-loader';
import { TasksGroupIcon } from 'features/nekst-widgets';
import useFeatureToggleService from 'feature-toggle';
import { get } from 'lodash';
import { PlanPermission, useAuthorizationChecker } from 'authorization-scope';

export interface TasksGroupsDropdownProps extends BaseInputProps<number> {
  planId: number;
}

export function TasksGroupsDropdown(props: TasksGroupsDropdownProps) {

  const tasksGroupsService = useTasksGroupsService();
  const tasksGroupsAdminService = useTasksGroupsAdminService();

  const { isGranted } = useAuthorizationChecker();

  const { data: groups } = useDataLoaderNew(() => {
    return tasksGroupsService.getTasksGroupsForPlan(props.planId);
  });

  const formContext = useFormContextRequired();
  const featureToggleService = useFeatureToggleService();

  const mapItem = (item: TasksGroup) => ({
    labelWidget: <TasksGroupIcon group={item} withName />,
    label: item.name,
    value: item.id,
    isUsed: item.isUsed
  });

  if (groups) {
    const value = get(formContext.data, props.source);

    const usedGroups = groups.filter((group) => group.isUsed || group.id === value);
    const notUsedGroups = groups.filter((group) => !group.isUsed);

    let options = [];

    if (usedGroups.length && notUsedGroups.length && featureToggleService.isNewTransactionsManagementEnabled()) {
      options.push({
        label: 'Current Market Groups',
        options: usedGroups.map(mapItem)
      });

      options.push({
        label: 'Other Groups',
        options: notUsedGroups.map(mapItem)
      });
    } else {
      options = groups.map(mapItem);
    }

    const optionCreateFunc = featureToggleService.isNewTransactionsManagementEnabled()
      ? async (groupName: string) => {
        let result: TasksGroup;
        if (isGranted(PlanPermission.IS_GLOBAL)) {
          result = await tasksGroupsAdminService.createTasksGroup({
            name: groupName,
            color: '#000000'
          });
        } else {
          result = await tasksGroupsService.createTaskGroup({
            name: groupName,
            color: '#000000',
            isHidden: false
          });
        }

        return mapItem(result);
      } : undefined;

    return (
      <Dropdown<number | null>
        {...props}
        options={options}
        placeholder="No Group"
        optionCreateFunc={optionCreateFunc}
        label={props.label}
      />
    );
  } else {
    return (
      <LoadingAnimation />
    );
  }

}
